import { css } from '@emotion/react'
import { format } from 'date-fns'
import { HeadProps, PageProps, graphql } from 'gatsby'
import { Fragment } from 'react'

import { Calendar } from '@/features/calendar'
import { Seo } from '@/features/common/'
import { DatoLink } from '@/features/dato-link'
import { PageHero } from '@/features/page-sections'
import { mq } from '@/theme/mixins'
import { colors } from '@/theme/variables'

const AcademicCalendarPage = ({
  data: {
    datoCmsAcademicCalendarPage: page,
    allDatoCmsAcademicCalendarEvent,
  },
}: PageProps<Queries.AcademicCalendarPageQuery>): JSX.Element => {
  const filteredEvents = allDatoCmsAcademicCalendarEvent.nodes.reduce(
    (acc, event) => {
      const date = new Date(event?.date || '')
      const month = format(date, 'MMMM')
      const year = format(date, 'yyyy')
      const matchingMonthYear = acc.find(
        val => val.month === month && val.year === year
      )
      if (matchingMonthYear) {
        matchingMonthYear.events.push(event)
      } else {
        acc.push({ month: month, year: year, events: [event] })
      }

      return acc
    },
    [] as {
      month: string
      year: string
      events: Queries.AcademicCalendarEventFragment[]
    }[]
  )
  const styles = {
    section: css`
      padding: var(--row-72) var(--margin);
      display: grid;
      grid-template-columns: 1fr auto;
      column-gap: var(--margin);
      background: #fff;
      ${mq().ms} {
        grid-template-columns: 1fr;
      }
    `,
    error: css`
      min-height: 6em;
      display: grid;
      align-items: center;
    `,
    calendar: css`
      ${page?.links?.length === 0 &&
      css`
        grid-column: 1 / -1;
        max-width: 120ch;
      `}
      ${mq().ms} {
        order: 2;
      }
    `,
    links: css`
      margin-top: 2.5em;
      max-width: 24ch;
      ${mq().ms} {
        order: 1;
        margin-top: 0;
        max-width: 100%;
        display: flex;
        gap: 1.5em;
      }
    `,
    link: css`
      color: ${colors.red};
      font-size: var(--fs-18);
      @media (hover: hover) {
        &:hover {
          color: ${colors.redDark};
        }
      }
    `,
  }
  return (
    <Fragment>
      <PageHero
        heading={page?.pageHeading}
        heroImage={page?.heroImage}
      />
      <section css={styles.section}>
        <Calendar
          css={styles.calendar}
          eventsByMonth={filteredEvents}
          errorMessage={
            <div
              css={styles.error}
            >{`There are currently no upcoming Academic Calendar events. Please check back soon.`}</div>
          }
        />
        <div css={styles.links}>
          {page?.links?.map(link => (
            <DatoLink
              data={link}
              key={link?.id}
              showIcon
              css={styles.link}
            />
          ))}
        </div>
      </section>
    </Fragment>
  )
}

export const Head = ({
  data: { datoCmsAcademicCalendarPage: page },
}: HeadProps<Queries.AcademicCalendarPageQuery>) => (
  <Seo seoMetaTags={page?.seoMetaTags} />
)

export const data = graphql`
  query AcademicCalendarPage {
    datoCmsAcademicCalendarPage {
      pageHeading
      heroImage {
        ...PageHero
      }
      links {
        ... on DatoCmsExternalLink {
          ...ExternalLink
        }
      }
      seoMetaTags {
        tags
      }
    }
    allDatoCmsAcademicCalendarEvent(
      sort: { date: ASC } # filter: { isUpcoming: { eq: true } }
    ) {
      nodes {
        ...AcademicCalendarEvent
      }
    }
  }
`

export default AcademicCalendarPage
